<template>
  <div :class="['team-table', { disabled: loading }]">
    <v-data-table
      v-model="tableModel"
      :headers="showMenu ? headersWithMenu : headers"
      :items="teamList"
      :must-sort="loading"
      :class="{ 'show-menu': showMenu, integrated }"
      :loading="progressBar"
      :items-per-page="teamList.length || 20"
      :show-select="integrated"
      height="100%"
      return-object
      item-selectable="isSelectable"
      item-value="id"
    >
      <template #header.data-table-select>
        <v-checkbox-btn
          :model-value="allSelected"
          color="brandPrimary"
          :disabled="disableCheckAll"
          @update:model-value="handleSelectAll"
        />
      </template>
      <template
        v-for="head in headers"
        #[`header.${head.value}`]="{ column }"
        :key="head.value"
      >
        <v-hover
          v-slot="{ isHovering }"
        >
          <button
            :key="column.value"
            @click="column.sortable
              ? $emit('update-teams', {
                selectedSortOption: head.value,
                router: $router,
                descending: selectedSortOptionDesc,
              })
              : null"
          >
            {{ head.title }}
            <v-icon
              v-if="head.sortable && (isHovering || selectedSortOption.includes(head.value))"
              size="small"
              :icon="(selectedSortOptionDesc
                && selectedSortOption.substring(1) === head.value)
                ? '$chevron-up'
                : '$chevron-down'"
            />
            <InsightsTooltip
              v-if="head.tooltip"
              class="ml-2"
              :tooltip="{ key: head.title, html: head.tooltip }"
            />
          </button>
        </v-hover>
      </template>
      <template #item.data-table-select="{ item }">
        <v-checkbox-btn
          :model-value="selectedMembers.some(({ id }) => id === item.id)"
          color="brandPrimary"
          :disabled="!item.isSelectable"
          @update:model-value="() => handleSelect(item)"
        />
      </template>
      <template #[`item.name`]="{ item }">
        <h5 class="team-member-name overflow">
          {{ item.name }}
        </h5>
        <div class="email overflow">
          {{ item.email }}
        </div>
      </template>
      <template #[`item.roles`]="{ item }">
        <div class="cell-container">
          <div class="roles">
            <p
              v-for="(role, index) in roleMap(item.roles).displayedRoles"
              :key="index"
            >
              {{ role }}
            </p>
          </div>
          <RolesTooltip :roles="roleMap(item.roles).tooltipRoles" />
        </div>
      </template>
      <template #[`item.jobCount`]="{ item }">
        <router-link
          :to="{ name: 'draft', query: { user: item.id } }"
          class="job-count"
        >
          {{ item.jobCount }}
        </router-link>
      </template>
      <template #[`item.persona`]="{ item }">
        <div v-if="canTeamAdmin" class="h-100 d-flex align-center">
          <DpplSubMenu
            :margin="false"
            :selected-option="item.persona"
            :sub-menu-options="personaItems"
            :loading="updatingPersona === item.id"
            class="team-persona-dropdown"
            :class="{ 'not-specified': item.persona === 'Not Specified' }"
            :should-open-on-hover="false"
            reduce-font-size
            :chip-height="28"
            @submit-sub-menu="($event) => handlePersonaUpdate($event, item.id)"
          >
            <template v-if="updatingPersona === item.id" #label>
              <DpplPageLoader :size="20" :width="2" class="mr-1" />
            </template>
          </DpplSubMenu>
        </div>
        <div v-else>
          {{ item.persona }}
        </div>
      </template>
      <template #[`item.isAdmin`]="{ item }">
        <div :class="['cell-container', { disabled: item.status === 'disabled' }]">
          <TeamPermissionButtons
            :user="item"
            :disabled="item.status === 'disabled'"
            :can-team-admin="canManageTeamRoles"
            :loading="loading"
            :status="item.status"
            @submit-sub-menu="onSubmitSubMenu"
          />
        </div>
      </template>
      <template #[`item.jpRole`]="{ item }">
        <div :class="['cell-container', { disabled: item.status === 'disabled' }]">
          <TeamPermissionButtons
            :user="item"
            :disabled="item.status === 'disabled'"
            :loading="loading"
            :is-job-nav-permission="true"
            @submit-sub-menu="onSubmitJobNavSubMenu"
          />
        </div>
      </template>
      <template #[`item.status`]="{ item }">
        <div :class="['cell-container', { disabled: item.status === 'disabled' }]">
          <TeamInviteActions
            :user="item"
            :disabled="item.status === 'disabled'"
            :invited="invited"
            :invite-loading="inviteLoading"
            :invite-errors="inviteErrors"
            :status="item.status"
            :da-installed="item.daLastUsedAt"
            @invite-onboard="sendSingleOnboardingInvite"
            @invite-da="sendSingleDatapeopleAnywhereInvite"
          />
        </div>
      </template>
      <template
        v-if="showMenu"
        #[`item.action`]="{ item }"
      >
        <MenuList
          v-if="canRemoveSpecificUser(item)"
          :item="item"
          :menu-items="[{
            label: 'Remove User',
            name: 'showDeleteModal',
          }]"
          @menu-click="$emit('show-delete-modal', { item })"
        />
      </template>
      <template #bottom>
        <span />
      </template>
    </v-data-table>
    <div class="team-footer">
      <Pagination
        v-if="totalPages > 1"
        :model-value="page"
        :length="totalPages"
        @update:model-value="(value) => $emit('update-teams', {
          page: value,
          router: $router,
        })"
      />
    </div>
  </div>
</template>

<script>
import { getQueryParamsFromURL } from '@/helpers/routing';
import { mapState, mapActions } from 'pinia';
import { useAppPagination } from '@/stores/app/pagination';
import { useSettingsIntegrations } from '@/stores/settings/integrations';
import { useSettingsTeam } from '@/stores/settings/team';
import Pagination from '@/components/shared/Pagination.vue';
import MenuList from '@/components/settings/components/lists/MenuList.vue';
import TeamPermissionButtons from '@/components/settings/team/TeamPermissionButtons.vue';
import TeamInviteActions from '@/components/settings/team/TeamInviteActions.vue';
import RolesTooltip from '@/components/settings/team/RolesTooltip.vue';
import { useAppBase } from '@/stores/app/base';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import DpplSubMenu from '@/components/datapeople/DpplSubMenu.vue';
import { personaOptions } from '@/components/settings/account/persona';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { updatePersona } from '@/api/settings';
import DpplPageLoader from '@/components/datapeople/DpplPageLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { ROLE_KEY_EDITOR } from '@/helpers/constants/recruiterRoles';

export default {
  name: 'TeamTable',
  components: {
    MenuList,
    TeamPermissionButtons,
    TeamInviteActions,
    RolesTooltip,
    Pagination,
    InsightsTooltip,
    DpplSubMenu,
    DpplPageLoader,
  },
  props: {
    teamList: { type: Array, default: () => [] },
    canTeamAdmin: { type: Boolean, default: false },
    canManageTeamRoles: { type: Boolean, default: false },
    canRemoveUsers: { type: Boolean, default: false },
    integrated: { type: Boolean, default: false },
    progressBar: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ['update-teams', 'show-delete-modal', 'send-onboarding-emails', 'teams-tracking-event'],
  data() {
    return {
      updatingPersona: null,
    };
  },
  computed: {
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),
    ...mapState(useAppPagination, ['page', 'totalPages']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useSettingsTeam, [
      'selectedMembers',
      'selectedSortOption',
      'inviteLoading',
      'invited',
      'inviteErrors',
      'headers',
      'headersWithMenu',
      'selectedSortOptionDesc',
    ]),
    ...mapState(useSettingsIntegrations, ['allowIntegratedUserDelete']),
    isItAdminActor() {
      return this.authData.rbac?.roleNames?.includes('itAdmin');
    },
    personaItems() {
      return Object.keys(personaOptions).map((key) => ({
        value: personaOptions[key],
        id: key,
      }));
    },
    allSelected() {
      return !this.disableCheckAll && this.teamList
        .filter(({ isSelectable }) => Boolean(isSelectable))
        .every(({ id }) => this.selectedMembers
          .find(({ id: memberId }) => id === memberId));
    },
    disableCheckAll() {
      return this.teamList.every(({ isSelectable }) => !isSelectable);
    },
    tableModel: {
      get() {
        return this.selectedMembers;
      },
      set(value) {
        this.setSelectedMembers(value);
      },
    },
    showMenu() {
      // Can remove users permission and can remove specific user
      return this.canRemoveUsers && this.teamList.some((user) => this.canRemoveSpecificUser(user));
    },
    paginationPage: {
      get() {
        return this.page;
      },
      set(newPage) {
        this.handlePageUpdate(newPage);
      },
    },
  },
  watch: {
    browserBackOrForwardNavigation: {
      immediate: true,
      handler(newValue) {
        if (newValue && window.location.pathname === '/app/settings/team') {
          const searchParams = getQueryParamsFromURL();

          if (this.paginationPage !== searchParams?.page) this.setPageNumber(searchParams.page);
        }
      },
    },
  },
  mounted() {
    this.setSelectedMembers();
  },
  methods: {
    ...mapActions(useAppPagination, ['setPageNumber']),
    ...mapActions(useAppLoading, ['setLoading']),
    ...mapActions(useSettingsTeam, [
      'setSelectedMembers',
      'submit',
      'submitJobNav',
      'sendOnboardingInvite',
      'sendDatapeopleAnywhereInvite',
      'setTeamList',
      'loadTeam',
    ]),
    ...mapActions(useAppSnackbar, ['addSuccessSnackbar', 'addErrorSnackbar']),
    async handlePersonaUpdate(personaKey, userId) {
      try {
        this.updatingPersona = userId;
        this.setLoading({ filtersLoading: true, listLoading: true });

        await updatePersona(personaKey, userId);

        await this.loadTeam({
          router: this.$router,
          routeQuery: this.$route.query,
          page: this.$route.query?.page || 1,
          selectedSortOption: this.$route.query?.sort || 'name',
          searchTerm: this.$route.query?.q || '',
        });

        this.addSuccessSnackbar({
          text: 'User persona has been updated',
        });
      } catch (error) {
        this.addErrorSnackbar({
          text: 'Failed to update user persona',
        });
      } finally {
        this.updatingPersona = null;
      }
    },
    handleSelect(item) {
      if (this.selectedMembers.find(({ id }) => id === item.id)) {
        this.setSelectedMembers(this.selectedMembers.filter(({ id }) => id !== item.id));
      } else {
        this.setSelectedMembers([...this.selectedMembers, item]);
      }
    },
    handleSelectAll() {
      if (this.allSelected) {
        this.setSelectedMembers([]);
      } else {
        this.setSelectedMembers(this.teamList
          .filter(({ isSelectable }) => Boolean(isSelectable)));
      }
    },
    sendSingleOnboardingInvite(event) {
      this.$emit('teams-tracking-event', { action: 'singleInviteSent', args: { id: event } });
      this.sendOnboardingInvite(event);
    },
    sendSingleDatapeopleAnywhereInvite(event) {
      this.$emit('teams-tracking-event', { action: 'singleDatapeopleAnywhereInviteSent', args: { id: event } });
      this.sendDatapeopleAnywhereInvite(event);
    },
    handlePageUpdate(page) {
      this.setPageNumber(page);
      this.$router.push({ query: { ...this.$route.query, page } }).catch(() => false);
    },
    roleMap(roles) {
      const roleMap = roles.map((role) => {
        if (role === 'hiring_manager') return 'Hiring Manager';
        return role.charAt(0).toUpperCase() + role.slice(1);
      });
      const [first = null, second = null, ...tooltipRoles] = roleMap;
      return { displayedRoles: { first, second }, tooltipRoles };
    },
    onSubmitSubMenu(event) {
      this.submit({
        ...event,
        routeQuery: this.$route.query,
      });
    },
    onSubmitJobNavSubMenu(event) {
      this.submitJobNav({
        ...event,
        routeQuery: this.$route.query,
      });
    },
    canRemoveSpecificUser(item) {
      if (item.status === 'disabled') return false;
      if (item.hasAtsId && !this.allowIntegratedUserDelete) return false;

      // Remove hierarchy only applies when role management is restricted to IT
      // Admins: Admin actors can then only remove Editors, IT Admins/staff can
      // remove anyone. With the flag off, removal is unrestricted, matching the server.
      if (!this.authData.isRoleManagementRestricted) return true;
      if (this.isItAdminActor) return true;

      return item.roleKey === ROLE_KEY_EDITOR;
    },
  },
};
</script>

<style scoped lang="less">
@import '@/less/mixins';
.team-table {
  border-radius: 8px;

  .team-header {
    padding: 24px;
    display: flex;
    justify-content: space-between;

    .right {
      display: flex;
      align-items: center;
    }
  }
  .team-footer {
    display: flex;
    justify-content: flex-end;
  }
  :deep(.v-data-table__wrapper) {
    overflow-x: hidden;
  }
  :deep(thead) {
    tr {
      background: @bg-input;
      max-height: 40px !important;
      height: 40px !important;

      &:hover {
        background: @bg-input !important;
        box-shadow: none !important;
      }

      th {
        padding: 0;
        height: 40px !important;
      }

      button:last-child {
        cursor: default;
      }

      button {
        height: 100%;
        width: 100%;
        text-align: left;
        padding: 0;
        font-size: 12px;
        line-height: 18px;
        color: @text-light;
        display: flex;
        align-items: center;

        &:hover {
          color: @text-darkest;
        }
      }
    }
  }

  :deep(tr) {
    .v-icon {
      position: unset;
    }

    &.v-data-table__selected {
      background: @white !important;
    }

    height: 64px;
    margin-bottom: 2px;
    &:hover {
      box-shadow: 0px 10px 20px @bg-canvas;
      background: @white !important;
    }

    td {
      font-size: 16px !important;
      width: auto;

      h5.team-member-name {
        max-width: 235px;
        font-size: 13px;
        line-height: 150%;
        font-weight: 500;
      }

      div.email {
        max-width: 525px;
        font-size: 12px;
        line-height: 150%;
      }

      div.cell-container {
        display: flex;
        align-content: center;
        white-space: nowrap;

        .roles {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          flex-wrap: wrap;

          p {
            font-size: 12px;
            line-height: 150%;
            color: @text-dark;
            font-weight: 500;
          }

          .info-icon {
            margin-left: 8px;
          }
        }
      }
    }
  }
  .integrated {
    :deep(thead.v-data-table-header) {
      th:first-child{
        padding-left: 16px !important;
      }
    }

    :deep(tbody) {
      tr {
        td:first-child {
          padding-right: 0;
        }

        td:nth-child(2) {
          padding: 0 16px;
        }

        td > div.email {
          max-width: 300px;
        }

        td > h5.team-member-name {
          max-width: 155px;
        }
      }
    }
  }
  :deep(.v-simple-checkbox) {
    position: unset;

    .v-input--selection-controls__input {
      position: unset;

      .v-input--selection-controls__ripple{
        position: relative;
      }
    }
  }
  .overflow {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status-dot {
    border-radius: 50%;
    height: 12px;
    width: 12px;
    background-color: @basic-green;
    margin-right: auto;
  }

  :deep(.v-selection-control__input)::before {
    color: @text-brand;
  }

  :deep(.v-selection-control) i {
    color: @text-light;
    font-size: 21px;
  }

  :deep(.v-selection-control--dirty) i {
    color: @text-brand;
  }
}

.invite-button {
  margin-top: 16px;
  padding: 0 40px;

  :deep(.v-btn__content) {
    font-weight: 400;
  }

  &.v-btn {
    background-color: @white !important;
    .shadow();
  }

  &:hover::before {
    background-color: @text-brand;
    opacity: 1;
  }
}
.show-menu {
  :deep(tr) {
    td:last-child {
      padding: 0;
      text-align: center;

      .v-btn {
        width: 100%;
      }
    }
  }
}

.job-count {
  font-size: 12px;
  color: @text-brand;
  line-height: 150%;
  font-weight: 500;
}

:deep(.team-persona-dropdown) .v-chip__content .p6 {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
}

:deep(.team-persona-dropdown).v-chip {
  border: 1px solid @stroke-default !important;

  &.not-specified:not(:hover) {
    background-color: @white !important;
  }
}
</style>
